/**
 * @fileoverview Business Logic (BL) for the Buzzer Battle game dynamic ("De barrio en barrio").
 * Encapsulates security checks, input sanitization, data integrity validations (SQL injection,
 * regex patterns, question set rules), and delegates operations to the Data Layer (DL).
 * 
 * @module utilities/BL/BuzzerBattle
 */

import * as DataLayerBuzzerBattle from './../DL/BuzzerBattle';
import { ValidateSqlInjection } from './Security';

// =============================================================================
// THEMES BUSINESS LOGIC & VALIDATIONS
// =============================================================================

/**
 * Retrieves all registered themes for Buzzer Battle.
 * 
 * @async
 * @function GetThemes
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Response payload.
 */
export const GetThemes = async () => {
    try {
        return await DataLayerBuzzerBattle.GetThemes();
    } catch (error) {
        return { success: false, message: error.message || 'Error al procesar la obtención de temas.' };
    }
};

/**
 * Retrieves a single theme by its unique numeric identifier after validation.
 * 
 * @async
 * @function GetTheme
 * @param {number|string} themeIdentifier - Unique theme ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload.
 */
export const GetTheme = async (themeIdentifier) => {
    try {
        const parsedThemeId = Number(themeIdentifier);
        if (!parsedThemeId || isNaN(parsedThemeId) || parsedThemeId <= 0) {
            return { success: false, message: 'El identificador del tema es inválido.' };
        }
        return await DataLayerBuzzerBattle.GetTheme(parsedThemeId);
    } catch (error) {
        return { success: false, message: error.message || 'Error al procesar la consulta del tema.' };
    }
};

/**
 * Validates theme attributes against security hazards and business constraints before saving.
 * 
 * @async
 * @function SaveTheme
 * @param {Object} themeData - Theme payload object.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload.
 */
export const SaveTheme = async (themeData) => {
    try {
        if (!themeData || typeof themeData !== 'object') {
            return { success: false, message: 'La información del tema es obligatoria.' };
        }

        // Validate Theme Name
        if (!themeData.name || typeof themeData.name !== 'string' || themeData.name.trim() === '') {
            return { success: false, message: 'El nombre del tema es obligatorio.' };
        }

        if (themeData.name.trim().length > 100) {
            return { success: false, message: 'El nombre del tema no puede exceder 100 caracteres.' };
        }

        const isNameThreat = await ValidateSqlInjection(themeData.name);
        if (isNameThreat) {
            return { success: false, message: 'Se detectó un patrón inseguro en el nombre del tema.' };
        }

        // Validate Cover Title if provided
        if (themeData.coverTitle && typeof themeData.coverTitle === 'string') {
            if (themeData.coverTitle.trim().length > 150) {
                return { success: false, message: 'El título de portada no puede exceder 150 caracteres.' };
            }
            const isCoverThreat = await ValidateSqlInjection(themeData.coverTitle);
            if (isCoverThreat) {
                return { success: false, message: 'Se detectó un patrón inseguro en el título de la portada.' };
            }
        }

        // Validate Theme ID if editing
        if (themeData.id !== undefined && themeData.id !== null) {
            const parsedId = Number(themeData.id);
            if (isNaN(parsedId) || parsedId < 0) {
                return { success: false, message: 'El identificador del tema proporcionado es inválido.' };
            }
        }

        return await DataLayerBuzzerBattle.SaveTheme(themeData);
    } catch (error) {
        return { success: false, message: error.message || 'Error al validar y guardar el tema.' };
    }
};

/**
 * Deletes a theme by ID after validating its presence and numeric format.
 * 
 * @async
 * @function DeleteTheme
 * @param {number|string} themeIdentifier - Unique theme ID.
 * @returns {Promise<{success: boolean, message?: string}>} Response payload.
 */
export const DeleteTheme = async (themeIdentifier) => {
    try {
        const parsedThemeId = Number(themeIdentifier);
        if (!parsedThemeId || isNaN(parsedThemeId) || parsedThemeId <= 0) {
            return { success: false, message: 'El identificador del tema a eliminar es inválido.' };
        }
        return await DataLayerBuzzerBattle.DeleteTheme(parsedThemeId);
    } catch (error) {
        return { success: false, message: error.message || 'Error al procesar la eliminación del tema.' };
    }
};

/**
 * Clones an existing theme record after ID validation.
 * 
 * @async
 * @function CloneTheme
 * @param {number|string} themeIdentifier - Source theme ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload.
 */
export const CloneTheme = async (themeIdentifier) => {
    try {
        const parsedThemeId = Number(themeIdentifier);
        if (!parsedThemeId || isNaN(parsedThemeId) || parsedThemeId <= 0) {
            return { success: false, message: 'El identificador del tema a clonar es inválido.' };
        }
        return await DataLayerBuzzerBattle.CloneTheme(parsedThemeId);
    } catch (error) {
        return { success: false, message: error.message || 'Error al procesar la clonación del tema.' };
    }
};

// =============================================================================
// QUESTION SETS BUSINESS LOGIC & STRICT VALIDATIONS
// =============================================================================

/**
 * Retrieves all registered question sets.
 * 
 * @async
 * @function GetQuestionSets
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Response payload.
 */
export const GetQuestionSets = async () => {
    try {
        return await DataLayerBuzzerBattle.GetQuestionSets();
    } catch (error) {
        return { success: false, message: error.message || 'Error al obtener los bancos de preguntas.' };
    }
};

/**
 * Retrieves a single question set by its identifier.
 * 
 * @async
 * @function GetQuestionSet
 * @param {number|string} questionSetIdentifier - Unique question set ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload.
 */
export const GetQuestionSet = async (questionSetIdentifier) => {
    try {
        const parsedSetId = Number(questionSetIdentifier);
        if (!parsedSetId || isNaN(parsedSetId) || parsedSetId <= 0) {
            return { success: false, message: 'El identificador del banco de preguntas es inválido.' };
        }
        return await DataLayerBuzzerBattle.GetQuestionSet(parsedSetId);
    } catch (error) {
        return { success: false, message: error.message || 'Error al obtener el banco de preguntas.' };
    }
};

/**
 * Validates question set data integrity:
 * - Title is required, non-empty, and free of SQL injection.
 * - Description free of SQL injection.
 * - Each question must have non-empty instruction free of SQL injection.
 * - Each question must have at least 2 options.
 * - Each option must have non-empty text free of SQL injection.
 * - Exactly 1 option per question must have isCorrect === true.
 * 
 * @async
 * @function SaveQuestionSet
 * @param {Object} questionSetData - Question set payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload.
 */
export const SaveQuestionSet = async (questionSetData) => {
    try {
        if (!questionSetData || typeof questionSetData !== 'object') {
            return { success: false, message: 'La información del banco de preguntas es obligatoria.' };
        }

        // Validate Question Set Title
        if (!questionSetData.title || typeof questionSetData.title !== 'string' || questionSetData.title.trim() === '') {
            return { success: false, message: 'El título del banco de preguntas es obligatorio.' };
        }

        if (questionSetData.title.trim().length > 150) {
            return { success: false, message: 'El título del banco no puede exceder 150 caracteres.' };
        }

        const isTitleThreat = await ValidateSqlInjection(questionSetData.title);
        if (isTitleThreat) {
            return { success: false, message: 'Se detectó un patrón inseguro en el título del banco de preguntas.' };
        }

        // Validate Description if provided
        if (questionSetData.description && typeof questionSetData.description === 'string') {
            const isDescThreat = await ValidateSqlInjection(questionSetData.description);
            if (isDescThreat) {
                return { success: false, message: 'Se detectó un patrón inseguro en la descripción del banco de preguntas.' };
            }
        }

        // Validate Nested Questions & Options Integrity
        if (Array.isArray(questionSetData.questions)) {
            for (let qIndex = 0; qIndex < questionSetData.questions.length; qIndex++) {
                const question = questionSetData.questions[qIndex];
                const questionNumber = qIndex + 1;

                if (!question || typeof question !== 'object') {
                    return { success: false, message: `La información de la pregunta #${questionNumber} es inválida.` };
                }

                // Instruction validation
                if (!question.instruction || typeof question.instruction !== 'string' || question.instruction.trim() === '') {
                    return { success: false, message: `La pregunta #${questionNumber} debe contener una instrucción o texto de pregunta.` };
                }

                const isInstructionThreat = await ValidateSqlInjection(question.instruction);
                if (isInstructionThreat) {
                    return { success: false, message: `Se detectó un patrón inseguro en la instrucción de la pregunta #${questionNumber}.` };
                }

                // Options validation
                if (!Array.isArray(question.options) || question.options.length < 2) {
                    return { success: false, message: `La pregunta #${questionNumber} ("${question.instruction.substring(0, 30)}...") debe contener al menos 2 opciones de respuesta.` };
                }

                let correctOptionsCount = 0;

                for (let oIndex = 0; oIndex < question.options.length; oIndex++) {
                    const option = question.options[oIndex];
                    const optionNumber = oIndex + 1;

                    if (!option || typeof option !== 'object') {
                        return { success: false, message: `La opción #${optionNumber} de la pregunta #${questionNumber} es inválida.` };
                    }

                    if (!option.text || typeof option.text !== 'string' || option.text.trim() === '') {
                        return { success: false, message: `El texto de la opción #${optionNumber} en la pregunta #${questionNumber} es obligatorio.` };
                    }

                    const isOptionThreat = await ValidateSqlInjection(option.text);
                    if (isOptionThreat) {
                        return { success: false, message: `Se detectó un patrón inseguro en la opción #${optionNumber} de la pregunta #${questionNumber}.` };
                    }

                    if (option.isCorrect === true || option.isCorrect === 1 || option.isCorrect === 'true') {
                        correctOptionsCount += 1;
                    }
                }

                if (correctOptionsCount !== 1) {
                    return {
                        success: false,
                        message: `La pregunta #${questionNumber} debe tener exactamente una opción marcada como correcta (actualmente tiene ${correctOptionsCount}).`
                    };
                }
            }
        }

        return await DataLayerBuzzerBattle.SaveQuestionSet(questionSetData);
    } catch (error) {
        return { success: false, message: error.message || 'Error al validar y guardar el banco de preguntas.' };
    }
};

/**
 * Deletes a question set by ID after validation.
 * 
 * @async
 * @function DeleteQuestionSet
 * @param {number|string} questionSetIdentifier - Unique question set ID.
 * @returns {Promise<{success: boolean, message?: string}>} Response payload.
 */
export const DeleteQuestionSet = async (questionSetIdentifier) => {
    try {
        const parsedSetId = Number(questionSetIdentifier);
        if (!parsedSetId || isNaN(parsedSetId) || parsedSetId <= 0) {
            return { success: false, message: 'El identificador del banco de preguntas a eliminar es inválido.' };
        }
        return await DataLayerBuzzerBattle.DeleteQuestionSet(parsedSetId);
    } catch (error) {
        return { success: false, message: error.message || 'Error al eliminar el banco de preguntas.' };
    }
};

/**
 * Clones a question set by ID after validation.
 * 
 * @async
 * @function CloneQuestionSet
 * @param {number|string} questionSetIdentifier - Source question set ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload.
 */
export const CloneQuestionSet = async (questionSetIdentifier) => {
    try {
        const parsedSetId = Number(questionSetIdentifier);
        if (!parsedSetId || isNaN(parsedSetId) || parsedSetId <= 0) {
            return { success: false, message: 'El identificador del banco de preguntas a clonar es inválido.' };
        }
        return await DataLayerBuzzerBattle.CloneQuestionSet(parsedSetId);
    } catch (error) {
        return { success: false, message: error.message || 'Error al clonar el banco de preguntas.' };
    }
};

// =============================================================================
// MATCHES HISTORY & MEDIA UPLOAD HELPERS
// =============================================================================

/**
 * Retrieves the history of completed Buzzer Battle matches.
 * 
 * @async
 * @function GetMatchesHistory
 * @param {number} [queryLimit=50] - Number of matches to fetch.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Response payload.
 */
export const GetMatchesHistory = async (queryLimit = 50) => {
    try {
        const sanitizedLimit = Number(queryLimit) > 0 ? Number(queryLimit) : 50;
        return await DataLayerBuzzerBattle.GetMatchesHistory(sanitizedLimit);
    } catch (error) {
        return { success: false, message: error.message || 'Error al obtener el historial de partidas.' };
    }
};

/**
 * Validates and records the result of a match in database.
 * 
 * @async
 * @function SaveMatch
 * @param {Object} matchData - Match parameters.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload.
 */
export const SaveMatch = async (matchData) => {
    try {
        if (!matchData || typeof matchData !== 'object') {
            return { success: false, message: 'Los datos de la partida son obligatorios.' };
        }
        return await DataLayerBuzzerBattle.SaveMatch(matchData);
    } catch (error) {
        return { success: false, message: error.message || 'Error al registrar la partida.' };
    }
};

/**
 * Packages a File or Blob into FormData with an optional category and uploads it.
 * 
 * @async
 * @function UploadMedia
 * @param {File|Blob} fileObject - Media file to upload.
 * @param {string} [mediaCategory=''] - Media category ('avatar', 'background', 'logo', 'question').
 * @returns {Promise<{success: boolean, url?: string, message?: string}>} Upload response payload.
 */
export const UploadMedia = async (fileObject, mediaCategory = '') => {
    try {
        if (!fileObject) {
            return { success: false, message: 'No se ha seleccionado ningún archivo multimedia para subir.' };
        }

        const multipartFormData = new FormData();
        multipartFormData.append('file', fileObject);
        if (mediaCategory) {
            multipartFormData.append('type', mediaCategory);
        }

        return await DataLayerBuzzerBattle.UploadMedia(multipartFormData);
    } catch (error) {
        return { success: false, message: error.message || 'Error al empaquetar y subir el archivo multimedia.' };
    }
};

/**
 * Packages an array or FileList of files into FormData with category and uploads them in batch.
 * 
 * @async
 * @function UploadBatchMedia
 * @param {Array<File|Blob>|FileList} filesCollection - Collection of media files.
 * @param {string} [mediaCategory=''] - Media category.
 * @returns {Promise<{success: boolean, urls?: Array<string>, count?: number, message?: string}>} Batch upload response payload.
 */
export const UploadBatchMedia = async (filesCollection, mediaCategory = '') => {
    try {
        if (!filesCollection || filesCollection.length === 0) {
            return { success: false, message: 'No se han proporcionado archivos para la carga en lote.' };
        }

        const multipartFormData = new FormData();
        for (let fileIndex = 0; fileIndex < filesCollection.length; fileIndex++) {
            multipartFormData.append('files', filesCollection[fileIndex]);
        }
        if (mediaCategory) {
            multipartFormData.append('type', mediaCategory);
        }

        return await DataLayerBuzzerBattle.UploadBatchMedia(multipartFormData);
    } catch (error) {
        return { success: false, message: error.message || 'Error al procesar la carga en lote de archivos.' };
    }
};

// =============================================================================
// REALTIME BACKUP DELEGATORS (REST ENGINE FALLBACK)
// =============================================================================

/**
 * Emits command to project the cover promotional screen.
 * 
 * @async
 * @function ShowCover
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const ShowCover = async () => {
    try {
        return await DataLayerBuzzerBattle.ShowCover();
    } catch (error) {
        return { success: false, message: error.message || 'Error al proyectar la portada.' };
    }
};

/**
 * Emits command to project the interactive combat arena HUD screen.
 * 
 * @async
 * @function ShowGame
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const ShowGame = async () => {
    try {
        return await DataLayerBuzzerBattle.ShowGame();
    } catch (error) {
        return { success: false, message: error.message || 'Error al proyectar el juego.' };
    }
};

/**
 * Initializes a new match with theme, question set, and custom players after validation.
 * 
 * @async
 * @function InitMatch
 * @param {number|string} themeIdentifier - Theme ID.
 * @param {number|string} questionSetIdentifier - Question set ID.
 * @param {Array<Object>} [customPlayers=null] - Optional customized player configurations.
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const InitMatch = async (themeIdentifier, questionSetIdentifier, customPlayers = null) => {
    try {
        const parsedThemeId = Number(themeIdentifier);
        const parsedSetId = Number(questionSetIdentifier);

        if (!parsedThemeId || isNaN(parsedThemeId) || parsedThemeId <= 0) {
            return { success: false, message: 'El identificador del tema es obligatorio e inválido.' };
        }

        if (!parsedSetId || isNaN(parsedSetId) || parsedSetId <= 0) {
            return { success: false, message: 'El identificador del banco de preguntas es obligatorio e inválido.' };
        }

        return await DataLayerBuzzerBattle.InitMatch({
            themeId: parsedThemeId,
            questionSetId: parsedSetId,
            customPlayers: Array.isArray(customPlayers) ? customPlayers : null
        });
    } catch (error) {
        return { success: false, message: error.message || 'Error al inicializar la partida.' };
    }
};

/**
 * Launches the question to stadium overlay and fan mobile screens.
 * 
 * @async
 * @function LaunchQuestion
 * @param {number|null} [questionIndex=null] - Zero-based question index.
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const LaunchQuestion = async (questionIndex = null) => {
    try {
        const sanitizedIndex = (questionIndex !== null && questionIndex !== undefined && !isNaN(Number(questionIndex)))
            ? Number(questionIndex)
            : null;
        return await DataLayerBuzzerBattle.LaunchQuestion(sanitizedIndex);
    } catch (error) {
        return { success: false, message: error.message || 'Error al lanzar la pregunta.' };
    }
};

/**
 * Displays answer options.
 * 
 * @async
 * @function ShowOptions
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const ShowOptions = async () => {
    try {
        return await DataLayerBuzzerBattle.ShowOptions();
    } catch (error) {
        return { success: false, message: error.message || 'Error al mostrar las opciones.' };
    }
};

/**
 * Assigns answering turn to a specific player ID after validation.
 * 
 * @async
 * @function AssignTurn
 * @param {string} playerIdentifier - Player ID / keycode.
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const AssignTurn = async (playerIdentifier) => {
    try {
        if (!playerIdentifier || typeof playerIdentifier !== 'string' || playerIdentifier.trim() === '') {
            return { success: false, message: 'El identificador del jugador es obligatorio.' };
        }
        return await DataLayerBuzzerBattle.AssignTurn(playerIdentifier.trim());
    } catch (error) {
        return { success: false, message: error.message || 'Error al asignar el turno.' };
    }
};

/**
 * Selects an option via HTTP REST after validation.
 * 
 * @async
 * @function SelectOption
 * @param {number|string} optionIdentifier - Option ID.
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const SelectOption = async (optionIdentifier) => {
    try {
        const parsedOptionId = Number(optionIdentifier);
        if (!parsedOptionId || isNaN(parsedOptionId) || parsedOptionId <= 0) {
            return { success: false, message: 'El identificador de la opción es inválido.' };
        }
        return await DataLayerBuzzerBattle.SelectOption(parsedOptionId);
    } catch (error) {
        return { success: false, message: error.message || 'Error al seleccionar la opción.' };
    }
};

/**
 * Manually selects an option by operator ID after validation.
 * 
 * @async
 * @function ManualSelectOption
 * @param {number|string} optionIdentifier - Option ID.
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const ManualSelectOption = async (optionIdentifier) => {
    try {
        const parsedOptionId = Number(optionIdentifier);
        if (!parsedOptionId || isNaN(parsedOptionId) || parsedOptionId <= 0) {
            return { success: false, message: 'El identificador de la opción es inválido.' };
        }
        return await DataLayerBuzzerBattle.ManualSelectOption(parsedOptionId);
    } catch (error) {
        return { success: false, message: error.message || 'Error al seleccionar manualmente la opción.' };
    }
};

/**
 * Clears selected answer.
 * 
 * @async
 * @function ClearSelectedOption
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const ClearSelectedOption = async () => {
    try {
        return await DataLayerBuzzerBattle.ClearSelectedOption();
    } catch (error) {
        return { success: false, message: error.message || 'Error al limpiar la opción seleccionada.' };
    }
};

/**
 * Reveals the verdict of the selected option.
 * 
 * @async
 * @function RevealResult
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const RevealResult = async () => {
    try {
        return await DataLayerBuzzerBattle.RevealResult();
    } catch (error) {
        return { success: false, message: error.message || 'Error al revelar el resultado.' };
    }
};

/**
 * Grants rebound to another player.
 * 
 * @async
 * @function GiveRebound
 * @param {string} nextPlayerIdentifier - Next player ID.
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const GiveRebound = async (nextPlayerIdentifier) => {
    try {
        if (!nextPlayerIdentifier || typeof nextPlayerIdentifier !== 'string' || nextPlayerIdentifier.trim() === '') {
            return { success: false, message: 'El identificador del siguiente jugador es obligatorio.' };
        }
        return await DataLayerBuzzerBattle.GiveRebound(nextPlayerIdentifier.trim());
    } catch (error) {
        return { success: false, message: error.message || 'Error al otorgar el rebote.' };
    }
};

/**
 * Advances to the next question or finishes match.
 * 
 * @async
 * @function NextQuestion
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const NextQuestion = async () => {
    try {
        return await DataLayerBuzzerBattle.NextQuestion();
    } catch (error) {
        return { success: false, message: error.message || 'Error al avanzar de pregunta.' };
    }
};

/**
 * Rewinds to the previous question (available before starting the match).
 * 
 * @async
 * @function PreviousQuestion
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const PreviousQuestion = async () => {
    try {
        return await DataLayerBuzzerBattle.PreviousQuestion();
    } catch (error) {
        return { success: false, message: error.message || 'Error al retroceder de pregunta.' };
    }
};

/**
 * Manually adjusts a participant's score (+1 or -1).
 * 
 * @async
 * @function AdjustPlayerScore
 * @param {string} playerIdentifier - Player ID (e.g. 'J1', 'J2').
 * @param {number} delta - Points delta (+1 or -1).
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const AdjustPlayerScore = async (playerIdentifier, delta) => {
    try {
        if (!playerIdentifier || typeof playerIdentifier !== 'string' || playerIdentifier.trim() === '') {
            return { success: false, message: 'El identificador del jugador es obligatorio.' };
        }
        const parsedDelta = Number(delta);
        if (isNaN(parsedDelta)) {
            return { success: false, message: 'El cambio de puntuación es inválido.' };
        }
        return await DataLayerBuzzerBattle.AdjustPlayerScore(playerIdentifier.trim(), parsedDelta);
    } catch (error) {
        return { success: false, message: error.message || 'Error al ajustar la puntuación.' };
    }
};

/**
 * Shows final results podium and triggers match persistence.
 * 
 * @async
 * @function ShowFinalResults
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const ShowFinalResults = async () => {
    try {
        return await DataLayerBuzzerBattle.ShowFinalResults();
    } catch (error) {
        return { success: false, message: error.message || 'Error al mostrar los resultados finales.' };
    }
};

/**
 * Resets the active match to idle state.
 * 
 * @async
 * @function ResetMatch
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const ResetMatch = async () => {
    try {
        return await DataLayerBuzzerBattle.ResetMatch();
    } catch (error) {
        return { success: false, message: error.message || 'Error al reiniciar la partida.' };
    }
};

/**
 * Forces answer as correct.
 * 
 * @async
 * @function ForceCorrect
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const ForceCorrect = async () => {
    try {
        return await DataLayerBuzzerBattle.ForceCorrect();
    } catch (error) {
        return { success: false, message: error.message || 'Error al forzar respuesta correcta.' };
    }
};

/**
 * Forces answer as incorrect.
 * 
 * @async
 * @function ForceIncorrect
 * @returns {Promise<{success: boolean, message?: string, data?: Object}>} Response payload.
 */
export const ForceIncorrect = async () => {
    try {
        return await DataLayerBuzzerBattle.ForceIncorrect();
    } catch (error) {
        return { success: false, message: error.message || 'Error al forzar respuesta incorrecta.' };
    }
};

/**
 * Retrieves the current real-time state.
 * 
 * @async
 * @function GetRealtimeState
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Response payload.
 */
export const GetRealtimeState = async () => {
    try {
        return await DataLayerBuzzerBattle.GetRealtimeState();
    } catch (error) {
        return { success: false, message: error.message || 'Error al consultar el estado en tiempo real.' };
    }
};

// =============================================================================
// PODIUM & SCORE CALCULATION HELPER
// =============================================================================

/**
 * Calculates podium standings, tie conditions, and the match winner.
 * 
 * @function CalculatePodium
 * @param {Array<Object>} [playersList=[]] - List of player state objects ({ name, score, ... }).
 * @param {string|Array<Object>|null} [finalScores=null] - Optional finalScores JSON string or array.
 * @returns {{
 *   winner: string,
 *   isTie: boolean,
 *   tiedPlayers: Array<Object>,
 *   rankedPlayers: Array<Object>,
 *   topScore: number
 * }} Calculated podium standings.
 */
export const CalculatePodium = (playersList = [], finalScores = null) => {
    try {
        let playersArray = [];

        if (Array.isArray(playersList) && playersList.length > 0) {
            playersArray = [...playersList];
        } else if (finalScores) {
            if (typeof finalScores === 'string') {
                try {
                    const parsed = JSON.parse(finalScores);
                    if (Array.isArray(parsed)) playersArray = parsed;
                } catch {
                    playersArray = [];
                }
            } else if (Array.isArray(finalScores)) {
                playersArray = [...finalScores];
            }
        }

        if (playersArray.length === 0) {
            return {
                winner: 'Sin Participantes',
                isTie: false,
                tiedPlayers: [],
                rankedPlayers: [],
                topScore: 0
            };
        }

        // Sort descending by score
        const rankedPlayers = playersArray
            .map((player) => ({
                ...player,
                score: Number(player.score) || 0
            }))
            .sort((playerA, playerB) => playerB.score - playerA.score);

        const topScore = rankedPlayers[0].score;
        const tiedPlayers = rankedPlayers.filter((player) => player.score === topScore);

        if (tiedPlayers.length > 1) {
            return {
                winner: 'Empate',
                isTie: true,
                tiedPlayers,
                rankedPlayers,
                topScore
            };
        }

        return {
            winner: tiedPlayers[0]?.name || 'Ganador Desconocido',
            isTie: false,
            tiedPlayers: [],
            rankedPlayers,
            topScore
        };
    } catch (calculationError) {
        return {
            winner: 'Error al calcular podio',
            isTie: false,
            tiedPlayers: [],
            rankedPlayers: [],
            topScore: 0
        };
    }
};
